import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';

const PatternRecognitionPanel = ({ errors, analysisHistory }) => {
  const [patterns, setPatterns] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    detectPatterns();
  }, [errors]);

  const detectPatterns = () => {
    setLoading(true);

    // Group errors by type and module
    const errorsByType = {};
    const errorsByModule = {};
    const errorsByTimeWindow = {};

    errors?.forEach(error => {
      // By type
      if (!errorsByType?.[error?.errorType]) {
        errorsByType[error?.errorType] = [];
      }
      errorsByType?.[error?.errorType]?.push(error);

      // By module
      if (error?.affectedModule) {
        if (!errorsByModule?.[error?.affectedModule]) {
          errorsByModule[error?.affectedModule] = [];
        }
        errorsByModule?.[error?.affectedModule]?.push(error);
      }

      // By time window (last hour)
      const hourKey = new Date(error?.occurredAt)?.toISOString()?.slice(0, 13);
      if (!errorsByTimeWindow?.[hourKey]) {
        errorsByTimeWindow[hourKey] = [];
      }
      errorsByTimeWindow?.[hourKey]?.push(error);
    });

    // Identify patterns
    const detectedPatterns = [];

    // Pattern 1: Recurring error types
    Object?.entries(errorsByType)?.forEach(([type, typeErrors]) => {
      if (typeErrors?.length >= 3) {
        detectedPatterns?.push({
          id: `type-${type}`,
          type: 'recurring_error',
          title: `Recurring ${type} Errors`,
          description: `${typeErrors?.length} instances detected`,
          severity: typeErrors?.[0]?.severity,
          occurrences: typeErrors?.length,
          affectedModules: [...new Set(typeErrors?.map(e => e?.affectedModule)?.filter(Boolean))],
          recommendation: 'Investigate common root cause across all instances',
          confidence: 92
        });
      }
    });

    // Pattern 2: Module-specific issues
    Object?.entries(errorsByModule)?.forEach(([module, moduleErrors]) => {
      if (moduleErrors?.length >= 3) {
        detectedPatterns?.push({
          id: `module-${module}`,
          type: 'module_issue',
          title: `${module} Module Issues`,
          description: `${moduleErrors?.length} errors in this module`,
          severity: 'high',
          occurrences: moduleErrors?.length,
          errorTypes: [...new Set(moduleErrors?.map(e => e?.errorType))],
          recommendation: 'Review module configuration and dependencies',
          confidence: 87
        });
      }
    });

    // Pattern 3: Error bursts
    Object?.entries(errorsByTimeWindow)?.forEach(([hour, hourErrors]) => {
      if (hourErrors?.length >= 5) {
        detectedPatterns?.push({
          id: `burst-${hour}`,
          type: 'error_burst',
          title: 'Error Burst Detected',
          description: `${hourErrors?.length} errors in 1-hour window`,
          severity: 'critical',
          occurrences: hourErrors?.length,
          timeWindow: new Date(hour)?.toLocaleString(),
          recommendation: 'Check for infrastructure issues or deployment changes',
          confidence: 95
        });
      }
    });

    setPatterns(detectedPatterns);
    setLoading(false);
  };

  const getPatternIcon = (type) => {
    const icons = {
      recurring_error: 'RefreshCw',
      module_issue: 'Package',
      error_burst: 'Zap',
      correlation: 'GitBranch'
    };
    return icons?.[type] || 'AlertTriangle';
  };

  const getPatternColor = (type) => {
    const colors = {
      recurring_error: 'from-orange-500 to-red-500',
      module_issue: 'from-purple-500 to-indigo-500',
      error_burst: 'from-red-500 to-pink-500',
      correlation: 'from-blue-500 to-cyan-500'
    };
    return colors?.[type] || 'from-gray-500 to-gray-600';
  };

  const getSeverityBadge = (severity) => {
    const colors = {
      critical: 'bg-red-100 text-red-700 border-red-200',
      high: 'bg-orange-100 text-orange-700 border-orange-200',
      medium: 'bg-yellow-100 text-yellow-700 border-yellow-200',
      low: 'bg-blue-100 text-blue-700 border-blue-200'
    };
    return colors?.[severity] || 'bg-gray-100 text-gray-700 border-gray-200';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Automated Pattern Detection</h3>
          <p className="text-sm text-gray-600 mt-1">AI-powered identification of recurring issues and systemic problems</p>
        </div>
        <button
          onClick={detectPatterns}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50"
        >
          <Icon name="RefreshCw" className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Re-analyze
        </button>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-lg p-4 border border-purple-200">
          <div className="flex items-center gap-3 mb-2">
            <Icon name="TrendingUp" className="w-5 h-5 text-purple-600" />
            <span className="text-sm font-medium text-gray-700">Patterns Detected</span>
          </div>
          <p className="text-2xl font-bold text-purple-600">{patterns?.length}</p>
        </div>
        <div className="bg-gradient-to-br from-orange-50 to-red-50 rounded-lg p-4 border border-orange-200">
          <div className="flex items-center gap-3 mb-2">
            <Icon name="AlertTriangle" className="w-5 h-5 text-orange-600" />
            <span className="text-sm font-medium text-gray-700">Total Errors</span>
          </div>
          <p className="text-2xl font-bold text-orange-600">{errors?.length}</p>
        </div>
        <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg p-4 border border-green-200">
          <div className="flex items-center gap-3 mb-2">
            <Icon name="Target" className="w-5 h-5 text-green-600" />
            <span className="text-sm font-medium text-gray-700">Avg Confidence</span>
          </div>
          <p className="text-2xl font-bold text-green-600">
            {patterns?.length > 0
              ? `${(patterns?.reduce((sum, p) => sum + p?.confidence, 0) / patterns?.length)?.toFixed(1)}%`
              : '0%'}
          </p>
        </div>
      </div>

      {/* Pattern List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Icon name="Loader" className="w-8 h-8 text-purple-600 animate-spin" />
        </div>
      ) : patterns?.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg border border-gray-200">
          <Icon name="CheckCircle" className="w-12 h-12 text-green-500 mx-auto mb-3" />
          <p className="text-gray-600 font-medium">No significant patterns detected</p>
          <p className="text-sm text-gray-500 mt-1">System is operating normally</p>
        </div>
      ) : (
        <div className="space-y-4">
          {patterns?.map((pattern) => (
            <div
              key={pattern?.id}
              className="bg-white border border-gray-200 rounded-lg p-5 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-start gap-3">
                  <div className={`p-2 bg-gradient-to-br ${getPatternColor(pattern?.type)} rounded-lg`}>
                    <Icon name={getPatternIcon(pattern?.type)} className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">{pattern?.title}</h4>
                    <p className="text-sm text-gray-600">{pattern?.description}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-xs font-medium px-2 py-1 rounded border ${getSeverityBadge(pattern?.severity)}`}>
                    {pattern?.severity?.toUpperCase()}
                  </span>
                  <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded">
                    {pattern?.confidence}% confidence
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-3">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Icon name="Hash" className="w-4 h-4" />
                  <span className="font-medium">{pattern?.occurrences}</span>
                  <span>occurrences</span>
                </div>
                {pattern?.affectedModules && (
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Icon name="Package" className="w-4 h-4" />
                    <span>{pattern?.affectedModules?.length} modules affected</span>
                  </div>
                )}
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <div className="flex items-start gap-2">
                  <Icon name="Lightbulb" className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-xs font-medium text-blue-900 mb-1">AI Recommendation</p>
                    <p className="text-sm text-blue-800">{pattern?.recommendation}</p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PatternRecognitionPanel;